import { createSelector } from '@reduxjs/toolkit';
import { AugmentedPhoneExtension } from '../../shared/services/contactsService';
import { ApiPhonebookEntry } from '../../shared/types/PhonebookEntry';
import { ApiVirtualExtension } from '../../shared/types/VirtualExtension';
import { StoreState } from '../store';

export const combinedContactsSelector = createSelector(
  [
    (state: StoreState) => state.contacts.phonebook,
    (state: StoreState) => state.contacts.phones,
    (state: StoreState) => state.contacts.virtuals,
  ],
  (phonebook, phones, virtuals) => [...phonebook, ...phones, ...virtuals],
);

export const combinedEndpointsSelector = createSelector(
  [
    (state: StoreState) => state.contacts.phones,
    (state: StoreState) => state.contacts.virtuals,
  ],
  (phones, virtuals) => [...phones, ...virtuals],
);

export const contactNamesByNumberSelector = createSelector(
  [
    (state: StoreState) => state.contacts.phonebook,
    (state: StoreState) => state.contacts.phones,
    (state: StoreState) => state.contacts.virtuals,
  ],
  (phonebook, phones, virtuals) => {
    const map: Record<string, string> = {};

    phonebook.forEach((x) => (map[x.phoneNumber] = x.name));
    phones.forEach((x) => (map[x.shortNumber] = x.name));
    virtuals.forEach((x) => (map[x.shortNumber] = x.name));
    return map;
  },
);

export const visibleContactsSelector = createSelector(
  [
    combinedContactsSelector,
    (state: StoreState) => state.contacts.visibilityFilter,
    (state: StoreState) => state.contacts.searchString,
  ],
  (allContacts, filter, searchString) => {
    let visibleContacts: (
      | ApiPhonebookEntry
      | AugmentedPhoneExtension
      | ApiVirtualExtension
    )[];

    // Get visible contacts based on visiblity filter
    switch (filter) {
      case 'SHOW_ALL': {
        visibleContacts = allContacts;
        break;
      }
      case 'SHOW_PHONEBOOK': {
        visibleContacts = allContacts.filter(
          (c) => c.type === 'phonebookentry',
        );
        break;
      }
      case 'SHOW_ENDPOINTS': {
        visibleContacts = allContacts.filter(
          (c) => c.type !== 'phonebookentry',
        );
        break;
      }
      default: {
        visibleContacts = allContacts;
        break;
      }
    }

    if (!searchString) {
      return visibleContacts.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    }

    return visibleContacts;
  },
);

export const availableExtensionsSelector = createSelector(
  [(state: StoreState) => state.contacts.phones],
  (phones) => phones.filter((x) => !x.readOnly && x.webRTCEnabled !== false),
);
